// ./src/utils/api/GuestGroupSetService.ts
//
// Guest-segmentation axes ("Teams", "Guest Type") — the top level of guest
// groups. Mirrors the eventsmagic-api GuestGroupSet SDL. `where.id` accepts one id
// or many — a bulk update (>1 id) may only change status (server-enforced). Each
// set holds VALUES (guest_group), managed via GuestGroupService.
import { gql } from '@apollo/client';
import { OpResult } from '@sdflc/api-helpers';

import { ApiService } from './ApiService';

const FIELDS = `
  id
  eventId
  name
  description
  color
  icon
  orderNo
  status
  rights
  groups { id status }
  createdAt
  updatedAt
`;

const LIST_QUERY = gql`
  query GuestGroupSetList($filter: GuestGroupSetFilter, $params: PaginationAndSorting) {
    guestGroupSetList(filter: $filter, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation GuestGroupSetCreate($params: GuestGroupSetInput) {
    guestGroupSetCreate(params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation GuestGroupSetUpdate($where: GuestGroupSetWhereInput, $params: GuestGroupSetInput) {
    guestGroupSetUpdate(where: $where, params: $params) {
      code
      data { ${FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation GuestGroupSetRemove($where: GuestGroupSetWhereInput) {
    guestGroupSetRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

export interface GuestGroupSet {
  id?: string;
  eventId?: string | null;
  name?: string | null;
  description?: string | null;
  color?: string | null;
  icon?: string | null;
  orderNo?: number | null;
  status?: number | null;
  rights?: string[] | null;
  /** The set's values (id + status only in the list — for a count badge). */
  groups?: { id?: string; status?: number | null }[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface GuestGroupSetInput {
  eventId?: string | null;
  name?: string | null;
  description?: string | null;
  color?: string | null;
  icon?: string | null;
  orderNo?: number | null;
  status?: number | null;
}

export class GuestGroupSetService {
  static async list(eventId: string, statuses?: number[]): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, {
      filter: { eventId: [eventId], status: statuses },
    });
  }

  static async create(params: GuestGroupSetInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  /** `id` may be a single id or an array (bulk — status-only). */
  static async update(id: string | string[], params: GuestGroupSetInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where: { id }, params });
  }

  static async remove(id: string | string[]): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }
}
